import {useSignal} from '@preact/signals-react'
import {useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {Sidebar} from 'primereact/sidebar'
import {forwardRef, useImperativeHandle, useMemo} from 'react'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../query-manager/QueryKeysManager'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {checkEmptyString} from '../../utils/CommonUtils'
import {useAuth} from '../authentication/AuthProvider'
import CustomForm from '../form-utils/CustomForm'
import {formLayout, getComposedDataList} from '../form/Constant'
import FormHeader from '../form/FormHeader'
import PageContentWrapper from '../layout/PageContentWrapper'
import {severityNotification} from '../notification/NotificationConstant'
import {showNotification} from '../notification/NotificationService'
import {generalPractitionerList} from './GeneralPractitionerContants'
import {useGeneralPractitionerCRUD} from './query-keys/GeneralPractitionerKeysManager'

const GeneralPractitionerForm = forwardRef((props, ref) => {
    useSignals()
    useImperativeHandle(ref, () => ({
        showForm: handleShowForm,
        hideForm: handleHideForm,
    }))

    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region Translate
    const {translateDatatable} = TranslateUtils()
    //#endregion

    //#region State
    const visible = useSignal(false)
    const data = useSignal({})
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        return getComposedDataList(generalPractitionerList, data.value)
    }, [data.value])
    //#endregion

    //#region Query
    const queryClient = useQueryClient()

    const {mutateAsync: createOrUpdateGeneralPractitioner} =
        useGeneralPractitionerCRUD(
            !!data.value?.id ? QueryCRUDTypes.update : QueryCRUDTypes.create,
            user?.id,
        )
    //#endregion

    //#region Handle Function
    const handleShowForm = value => {
        blockOverflownScroll()
        data.value = value && Object.keys(value)?.length > 0 ? {...value} : {}
        visible.value = true
    }
    const handleHideForm = () => {
        unblockOverflownScroll()
        data.value = {}
        visible.value = false
    }

    const handleOnSubmit = async value => {
        let isEmptyName = checkEmptyString(value?.name)
        if (!!isEmptyName) {
            showNotification(severityNotification.warn, 'Name is required !')
            return
        }

        if (!value.email) {
            showNotification(severityNotification.error, 'Email is required !!')
            return
        }

        let toPostData = {
            ...value,
            id: data.value?.id ? data.value.id : null,
            addressId: data.value?.addressId ? data.value.addressId : null,
        }

        const generalPractitionerData = await createOrUpdateGeneralPractitioner(
            toPostData,
        )

        if (generalPractitionerData?.id) {
            const {generalPractitionerKeys} = QueryKeyManager(
                QueryKeyTypes.generalPractitioner,
                user?.id,
            )

            //TODO: Temporary Refetch Table
            queryClient.invalidateQueries({
                queryKey: generalPractitionerKeys.pattern(),
            })

            // try {
            //     reloadDataTable(QueryKey, generalPractitionerData)
            // } catch (err) {
            //     console.log('Fail to reload table')
            // }

            handleHideForm()
        }
    }
    //#endregion

    return (
        <Sidebar
            className="p-sidebar-full"
            visible={visible.value}
            onHide={handleHideForm}
            blockScroll
            fullScreen>
            <PageContentWrapper>
                <FormHeader
                    header={translateDatatable('GENERAL PRACTITIONER FORM')}
                    onHide={handleHideForm}
                />
                <CustomForm
                    dataList={dataList}
                    layout={formLayout.group}
                    onSubmit={handleOnSubmit}
                />
            </PageContentWrapper>
        </Sidebar>
    )
})

export default GeneralPractitionerForm
